import { useState } from "react";
import { postFeedback } from "../realApi";

/**
 * Beyond Sure — answer feedback / capture (L04).
 *
 * Thumbs up/down on a REAL answer. A thumbs-down opens a suggestion box; submitting POSTs to
 * /api/feedback (the C2 capture pattern → training flywheel). Honest about the server's min-length
 * rule and transient errors — never silently drops the user's correction.
 *
 * Rendered only for answers the backend actually produced (available === true) — never on the
 * no-key gate, where there is nothing to rate.
 */
export function AnswerFeedback({ messageId }: { messageId: string }) {
  const [sent, setSent] = useState<null | "up" | "down">(null);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function vote(thumb: "up" | "down") {
    // First down-click just opens the suggestion box; the actual POST happens on "Send".
    if (thumb === "down" && !open) {
      setOpen(true);
      return;
    }
    setBusy(true);
    setErr(null);
    const res = await postFeedback({
      messageId,
      thumb,
      comment: thumb === "down" ? text.trim() : undefined,
    });
    setBusy(false);
    if (res.ok) {
      setSent(thumb);
      setOpen(false);
    } else {
      setErr(
        res.error === "comment_min_length"
          ? "Please add a little more detail (10+ characters)."
          : res.error === "login_required"
            ? "Please sign in to send feedback."
            : "Couldn’t send — please try again.",
      );
    }
  }

  if (sent) {
    return (
      <div className="mt-2 text-[11px] text-[var(--color-ink-40)]" data-testid="feedback-done">
        {sent === "down"
          ? "Thanks — your suggestion was captured for training."
          : "Thanks for the feedback."}
      </div>
    );
  }

  return (
    <div className="mt-2 flex flex-col gap-2" data-testid="answer-feedback">
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-[var(--color-ink-40)] mr-0.5">Was this helpful?</span>
        <button
          type="button"
          aria-label="Helpful"
          onClick={() => vote("up")}
          disabled={busy}
          className="inline-flex h-6 w-6 items-center justify-center rounded-[6px] text-[var(--color-ink-40)] hover:bg-[var(--color-ink-08)] hover:text-[var(--color-brand-deep)] transition-colors duration-[var(--duration-fast)]"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M7 10v10M7 10l4-7a2 2 0 0 1 3 1.5V9h4.5a2 2 0 0 1 2 2.4l-1.4 7a2 2 0 0 1-2 1.6H7"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          aria-label="Not helpful"
          onClick={() => vote("down")}
          disabled={busy}
          data-testid="thumb-down"
          className="inline-flex h-6 w-6 items-center justify-center rounded-[6px] text-[var(--color-ink-40)] hover:bg-[var(--color-ink-08)] hover:text-[#a23b3b] transition-colors duration-[var(--duration-fast)]"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M17 14V4M17 14l-4 7a2 2 0 0 1-3-1.5V15H5.5a2 2 0 0 1-2-2.4l1.4-7a2 2 0 0 1 2-1.6H17"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {open ? (
        <div className="flex flex-col gap-1.5" data-testid="suggestion-box">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            placeholder="What was wrong, or what’s the better answer? (helps train the brain)"
            className="w-full max-w-[460px] rounded-[10px] border border-[var(--color-ink-08)] bg-[var(--color-paper)] px-3 py-2 text-[13px] text-[var(--color-ink)] outline-none focus:border-[var(--color-brand-deep)]"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => vote("down")}
              disabled={busy || text.trim().length < 10}
              className="rounded-full px-3 py-1 text-[12px] font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: "var(--color-brand-deep)" }}
            >
              {busy ? "Sending…" : "Send suggestion"}
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setText("");
              }}
              disabled={busy}
              className="text-[12px] text-[var(--color-ink-40)] hover:text-[var(--color-ink-60)]"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {err ? (
        <div className="text-[11px] text-[#a23b3b]" data-testid="feedback-error">
          {err}
        </div>
      ) : null}
    </div>
  );
}

export default AnswerFeedback;
